/*
  Accent folding, shared by the home search (§4.2) and the A-Z grouping (§4.4).

  It is one helper on purpose: without folding, a game named "Édition" uppercases to
  "É", fails /^[A-Z]$/ and lands under "#" rather than under "E", while the search box
  would fail to match "edition" — two symptoms of the same missing rule (D17). A second
  implementation is how those two silently drift apart.
*/

/*
  NFD splits an accented character into its base letter plus a combining mark, so
  stripping the U+0300-U+036F block leaves the base letter. "Pokémon" -> "Pokemon".

  The trailing NFC recomposition matters for the games this site actually carries: NFD
  also decomposes Japanese kana, and the dakuten U+3099 sits outside the Latin block
  being stripped, so without recomposing, a kana name would come back visually identical
  but no longer equal to itself. Folding must only ever touch Latin accents.
*/
export const foldAccents = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .normalize("NFC");

/* The comparison form both sides of a search are reduced to before matching. */
export const normaliseSearchText = (value: string): string =>
  foldAccents(value).toLowerCase();

/*
  Substring match, accent-folded and case-insensitive on both sides.

  A whitespace-only query is treated as empty and matches everything (§4.2) — it is a
  user who has not typed anything meaningful yet, not a query nothing satisfies.
*/
export const matchesSearch = (value: string, query: string): boolean => {
  const needle = normaliseSearchText(query.trim());
  if (needle === "") {
    return true;
  }
  return normaliseSearchText(value).includes(needle);
};
